export interface TeamCreationIntent {
  format?: 'entries_v1';
  actions?: import('./workforce-team-editor.state').EditorAction[];
  membershipBaseline?: string;
  copyFrom?: {teamId: string; membershipBaseline: string};
  criteria?: import('./workforce-team-criteria.state').WorkforceCriteria;
  settingsUpdate?: {settings: {name: string; amount: number}; baseline: {name: string; amount: number}};
  criteriaUpdate?: {revision: number; criteria: import('./workforce-team-criteria.state').WorkforceCriteria};
  snapshotId: string; editMembers?: boolean; removalMemberIds?: string[]; employeeKeys?: string[]; teamName?: string; amount?: number; teamId?: string;
  selection?: {mode: 'all_matching'; criteria: import('./workforce-team-criteria.state').WorkforceCriteria; revision?: number};
}
export interface TeamCreationProgress {
  allowanceFunding?: {status: string; message: string; savedAt: string; operationId?: string};
  execution?: 'background';
  backgroundState?: 'ready' | 'paused' | 'done';
  targetTeamId?: string;
  pendingName?: string | null;
  purpose?: 'create' | 'add_members' | 'edit_members';
  removedCount?: number;
  criteriaSaved?: boolean;
  preflight?: {complete: boolean; format?: 'entries_v1'; [key: string]: number | boolean | string | undefined};
  operationId: string; phase: 'preflight' | 'preparing' | 'needs_review' | 'team_committed' | 'complete' | 'closed';
  selectedCount: number; preparedCount: number; memberCount: number; teamId: string | null;
  issues: string[]; message: string | null;
}
export interface TeamCreationApi {
  submitWorkforceTeam(companyId: string, request: TeamCreationIntent, key: string): Promise<TeamCreationProgress>;
  getWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress>;
  recoverWorkforceTeamCreations(companyId: string, after?: string): Promise<{operations: TeamCreationProgress[]; next?: string | null; backgroundAvailable?: boolean}>;
  resumeWorkforceTeamCreation?(companyId: string, operationId: string): Promise<TeamCreationProgress>;
  continueWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress>;
  closeWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress>;
}
export class WorkforceTeamCreationState {
  companyId = '';
  progress: TeamCreationProgress | null = null;
  recoverable: TeamCreationProgress[] = [];
  busy = false;
  error = '';
  operations: TeamCreationProgress[] = [];
  backgroundAvailable = false;
  pollError = '';
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private polling = false;
  private pollFailures = 0;
  private notified = new Set<string>();
  private actions = new Set<string>();
  private version = 0;
  private actionKey: string | null = null;
  private request: TeamCreationIntent | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private wake: (() => void) | null = null;
  constructor(private api: TeamCreationApi,
              private onComplete: (teamId: string) => void = () => {},
              private makeKey: () => string = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join(''),
              private onCommitted: (teamId: string) => Promise<void> | void = () => {},
              private onBackgroundCommitted: (teamId: string) => Promise<void> | void = () => {}) {}
  get progressMessage(): string {
    return this.messageFor(this.progress);
  }
  messageFor(p: TeamCreationProgress | null): string {
    if (!p) return '';
    if (p.execution === 'background' && p.backgroundState === 'ready' && p.issues.length) return 'Waiting for background processing to resume.';
    if (p.message) return p.message;
    if (p.purpose === 'edit_members' && ['complete', 'team_committed'].includes(p.phase)) { return p.phase === 'complete' ? 'Team changes saved.' : 'Team changes saved; finishing account setup.'; }
    if (p.phase === 'complete') return p.purpose === 'add_members' ? 'Employee additions completed.' : 'Team created.';
    if (p.phase === 'closed') return 'Recovery closed. Prepared accounts and committed memberships are retained.';
    if (p.phase === 'team_committed') { return p.purpose === 'add_members' ? 'Employees added; finishing account setup.' : 'Team created; finishing account setup.'; }
    if (p.phase === 'preflight') return 'Checking the complete intended cohort before preparing accounts.';
    if (p.phase === 'needs_review') return 'Review required. Account preparation may be partially completed; membership has not committed.';
    return 'Preparing selected accounts.';
  }
  stop(): void {
    ++this.version; this.busy = false; this.polling = false;
    if (this.pollingTimer) clearTimeout(this.pollingTimer);
    this.pollingTimer = null;
    if (this.timer) { clearTimeout(this.timer); }
    this.timer = null; this.wake?.(); this.wake = null;
  }
  destroy(): void { this.stop(); this.progress = null; this.recoverable = []; this.operations = []; this.notified.clear(); this.actions.clear(); this.backgroundAvailable = false; this.pollFailures = 0; this.pollError = ''; this.request = null; this.actionKey = null; }
  async open(companyId: string): Promise<void> {
    this.destroy(); this.companyId = companyId; this.error = '';
    const version = this.version;
    await this.poll(version, companyId);
  }
  get blocksNewSubmission(): boolean {
    return this.busy || !!this.progress && this.progress.execution !== 'background' && !['complete', 'closed'].includes(this.progress.phase);
  }
  get pendingCreates(): TeamCreationProgress[] {
    return this.operations.filter(p => p.execution === 'background' && p.purpose === 'create' && !p.teamId && p.phase !== 'closed');
  }
  forTeam(teamId: string | undefined): TeamCreationProgress | null {
    if (!teamId) return null;
    const candidates = [...this.operations].reverse().filter(p => (p.execution === 'background' || p.allowanceFunding)
      && (p.targetTeamId || p.teamId) === teamId);
    const active = candidates.find(p => !['complete', 'closed'].includes(p.phase));
    if (active) return active;
    const latestFunding = candidates.filter(p => p.allowanceFunding).sort((a, b) =>
      String(b.allowanceFunding!.savedAt).localeCompare(String(a.allowanceFunding!.savedAt))
      || b.operationId.localeCompare(a.operationId))[0]?.allowanceFunding;
    // A later name/member-only Save keeps its own progress and counts while
    // retaining the outcome of the last amount update on the same team.
    return candidates[0] ? {...candidates[0], ...(latestFunding ? {allowanceFunding: latestFunding} : {})} : null;
  }
  fundingVersionForTeam(teamId: string | undefined): string {
    if (!teamId) return '';
    const latest = this.operations.filter(p => p.allowanceFunding && (p.targetTeamId || p.teamId) === teamId)
      .sort((a, b) => String(b.allowanceFunding!.savedAt).localeCompare(String(a.allowanceFunding!.savedAt))
        || b.operationId.localeCompare(a.operationId))[0];
    return latest ? latest.operationId + ':' + latest.allowanceFunding!.status : '';
  }
  teamBusy(teamId: string | undefined): boolean {
    if (!teamId) return false;
    return this.operations.some(p => (p.targetTeamId || p.teamId) === teamId && !['complete', 'closed'].includes(p.phase));
  }
  actionBusy(id: string): boolean { return this.actions.has(id); }
  private remember(p: TeamCreationProgress): void {
    if (p.allowanceFunding && p.phase === 'complete' && this.progress?.phase === 'complete'
      && this.progress.operationId === p.operationId) this.progress = p;
    const index = this.operations.findIndex(item => item.operationId === p.operationId);
    if (index < 0) this.operations = [...this.operations, p];
    else this.operations = this.operations.map(item => item.operationId === p.operationId ? p : item);
  }
  private schedulePoll(force = false, delay = 2000): void {
    if (this.pollingTimer || !force && !(this.backgroundAvailable || this.operations.some(p => p.allowanceFunding?.status === 'pending'
      || !!p.allowanceFunding && !!p.teamId && !this.notified.has(p.operationId + ':' + p.phase)
      || p.execution === 'background' && (!['complete', 'closed'].includes(p.phase)
      || !!p.teamId && !this.notified.has(p.operationId + ':' + p.phase))))) return;
    const version = this.version, company = this.companyId;
    this.pollingTimer = setTimeout(() => { this.pollingTimer = null; void this.poll(version, company); }, delay);
  }
  private async poll(version: number, company: string): Promise<void> {
    if (version !== this.version || this.polling) return;
    this.polling = true;
    try {
      const recovered: TeamCreationProgress[] = [], cursors = new Set<string>();
      let after: string | undefined;
      do {
        const response = await this.api.recoverWorkforceTeamCreations(company, after);
        if (version !== this.version) return;
        this.backgroundAvailable = !!response.backgroundAvailable;
        recovered.push(...response.operations);
        after = response.next || undefined;
        if (after && cursors.has(after)) throw new Error('Repeated recovery cursor');
        if (after) cursors.add(after);
      } while (after);
      // Terminal operations disappear from recovery. Read their receipts before
      // removing pending cards or announcing completion.
      for (const previous of this.operations) {
        if (['complete', 'closed'].includes(previous.phase) || recovered.some(p => p.operationId === previous.operationId)) continue;
        const receipt = await this.api.getWorkforceTeamCreation(company, previous.operationId);
        if (version !== this.version) return;
        recovered.push(receipt);
      }
      if (version !== this.version) return;
      // Recovery returns the latest durable funding receipt per team. Retire
      // superseded receipts so an old pending result cannot poll forever.
      this.operations = this.operations.filter(p => !p.allowanceFunding || !['complete', 'closed'].includes(p.phase)
        || recovered.some(current => current.operationId === p.operationId));
      for (const p of recovered) this.remember(p);
      this.recoverable = recovered.filter(p => p.execution !== 'background' && !['complete', 'closed'].includes(p.phase));
      this.pollError = ''; this.pollFailures = 0;
      for (const p of this.operations) {
        if ((p.execution !== 'background' && !p.allowanceFunding) || !p.teamId || !['team_committed', 'complete', 'closed'].includes(p.phase)) continue;
        const key = p.operationId + ':' + p.phase;
        if (this.notified.has(key)) continue;
        try {
          await this.onBackgroundCommitted(p.teamId);
          if (version !== this.version) return;
          this.notified.add(key);
        } catch (error: any) {
          if (version !== this.version) return;
          if ([401, 403].includes(error?.status)) throw error;
          // Retry this team next poll without withholding other committed teams.
          this.pollError = 'Unable to refresh some teams. Accepted work is retained; retrying.';
        }
      }
    } catch (error: any) {
      if (version !== this.version) return;
      if ([401, 403].includes(error?.status)) {
        this.destroy(); this.error = 'Team progress is unavailable. Reload to resolve access.'; return;
      }
      this.pollFailures = Math.min(this.pollFailures + 1, 5);
      this.pollError = 'Unable to refresh team progress. Retrying automatically; accepted work is retained.';
    } finally {
      if (version === this.version) {
        this.polling = false;
        // Discovery must retry even before we know whether background work exists.
        const delay = Math.min(30000, 2000 * 2 ** Math.max(0, this.pollFailures - 1));
        this.schedulePoll(this.pollFailures > 0, delay);
      }
    }
  }
  private async backgroundAction(operationId: string, close = false): Promise<void> {
    if (this.actions.has(operationId)) return;
    const version = this.version, company = this.companyId;
    this.actions.add(operationId); this.error = '';
    try {
      const p = close ? await this.api.closeWorkforceTeamCreation(company, operationId)
        : await this.api.resumeWorkforceTeamCreation!(company, operationId);
      if (version !== this.version) return;
      this.remember(p);
      this.recoverable = this.recoverable.filter(item => item.operationId !== operationId);
      if (this.progress?.operationId === operationId) this.progress = p;
      this.schedulePoll(true);
    } catch {
      if (version === this.version) this.error = 'Unable to update recovery. Work is retained; refresh progress before trying again.';
    } finally { if (version === this.version) this.actions.delete(operationId); }
  }
  async submit(request: TeamCreationIntent, dialogOwnsRejection = false, waitForCompletion = false): Promise<void> {
    if (this.blocksNewSubmission) { throw new Error('Resume the current team creation first.'); }
    const serialized = JSON.stringify(request);
    if ((request.format !== 'entries_v1' && !request.selection && (!(request.employeeKeys?.length || request.editMembers && (request.removalMemberIds?.length || request.criteriaUpdate))
      || (request.employeeKeys?.length || 0) > 100)) || new TextEncoder().encode(serialized).byteLength > 65536) {
      throw new Error('Selection is outside the temporary V1 limits.');
    }
    if (this.request && JSON.stringify(this.request) !== serialized) { throw new Error('Resolve the previous submission before changing its details.'); }
    const submission: TeamCreationIntent = JSON.parse(serialized);
    this.request = submission; this.actionKey = this.actionKey || this.makeKey();
    this.progress = null; this.busy = true; this.error = '';
    const version = this.version; const company = this.companyId;
    try {
      const progress = await this.api.submitWorkforceTeam(company, submission, this.actionKey);
      if (version !== this.version) { return; }
      this.progress = progress; this.actionKey = null; this.request = null;
      this.busy = false;
      if (progress.execution === 'background') { this.remember(progress); this.schedulePoll(true); return; }
      if (waitForCompletion) await this.resume(progress.operationId);
      else void this.resume(progress.operationId);
    } catch (error: any) {
      if (version === this.version) {
        const code = error?.data?.code || error?.error?.code;
        const rejected: Record<string, string> = {
          membership_changed: 'A selected membership changed. Reopen the editor to review current members.',
          member_protected: 'A selected member is protected or already in a removal process.',
          member_previously_removed: 'A previously removed membership cannot be re-added in this editor.',
          preflight_blocked: 'A bounded identity check could not be completed. Review the account matches before submitting again.',
          criteria_invalid: 'Choose supported inclusion criteria or all eligible employees.',
          criteria_changed: 'Team filters changed. Reopen the editor to review the current version.',
          criteria_required: 'Save membership criteria before adding newly matching employees.',
          team_busy: 'Another membership operation is active for this team. Resume it or wait for it to finish.',
          team_not_managed: 'This command is available only for snapshot-created teams.',
          settings_changed: 'Team details changed. Reopen the editor to review the current values.',
          request_invalid: 'Check the team details and selection.',
          authority_changed: 'The selected snapshot or connection is no longer authorized. Reload the workforce and review the selection.',
          projection_unavailable: 'A selected employee lacks required onboarding evidence. Remove that employee or request a new enriched snapshot.',
          employee_ineligible: 'A selected employee is no longer eligible for team creation.',
          primary_email_required: 'A selected new employee needs a primary email before onboarding.',
          identity_conflict: 'Selected employee emails match more than one account. Resolve the match before creating the team.',
          brand_conflict: 'A selected employee needs account setup before they can join this company.',
          company_configuration_required: 'Account setup for this company is unavailable. Contact support before trying again.',
        };
        if (rejected[code] && [400, 409, 422].includes(error?.status)) {
          const body = error?.data || error?.error;
          const keys = body?.validation?.employeeKeys;
          const employeeKeys: string[] = code === 'brand_conflict' && body?.admission === 'rejected'
            && body?.validation?.kind === 'employee_account_setup' && !submission.selection
            && Array.isArray(keys) && keys.length > 0 && keys.length <= 100 && new Set(keys).size === keys.length
            && keys.every(value => typeof value === 'string' && submission.employeeKeys?.includes(value)) ? [...keys] : [];
          let message = employeeKeys.length
            ? `${employeeKeys.length} selected ${employeeKeys.length === 1 ? 'employee needs' : 'employees need'} account setup before they can join this company.`
            : rejected[code];
          if (body?.admission === 'rejected' && (employeeKeys.length || code === 'company_configuration_required')) {
            message += submission.editMembers ? ' Membership changes have not been saved.'
              : submission.teamId ? ' Employee additions have not been saved.' : ' The team has not been created.';
          }
          this.request = null; this.actionKey = null; this.error = dialogOwnsRejection ? '' : message;
          throw Object.assign(new Error(message), {submissionRejected: true, employeeKeys});
        }
        this.error = 'Submission could not be confirmed. Retry with the same details, or reload to find and resume the operation.';
        throw new Error(this.error);
      }
    } finally { if (version === this.version && !this.progress) { this.busy = false; } }
  }
  private hasAdvanced(before: TeamCreationProgress, after: TeamCreationProgress): boolean {
    if (before.operationId !== after.operationId || after.issues.length) { return false; }
    const rank: Partial<Record<TeamCreationProgress['phase'], number>> = {preflight: 0, preparing: 1, team_committed: 2, complete: 3};
    const previous = rank[before.phase]; const current = rank[after.phase];
    if (previous === undefined || current === undefined || current < previous) { return false; }
    if (current > previous || after.preparedCount > before.preparedCount || after.memberCount > before.memberCount) { return true; }
    if (before.phase !== 'preflight') { return false; }
    if (!before.preflight?.complete && after.preflight?.complete) { return true; }
    return Object.entries(after.preflight || {}).some(([key, value]) => {
      const prior = before.preflight?.[key];
      return typeof value === 'number' && Number.isFinite(value) && typeof prior === 'number' && Number.isFinite(prior) && value > prior;
    });
  }
  private delay(milliseconds: number): Promise<void> {
    return new Promise(resolve => { this.wake = resolve; this.timer = setTimeout(() => {
      this.timer = null; this.wake = null; resolve();
    }, milliseconds); });
  }
  async resume(operationId: string): Promise<void> {
    if (this.api.resumeWorkforceTeamCreation && (this.backgroundAvailable || this.operations.some(p => p.operationId === operationId && p.execution === 'background'))) {
      await this.backgroundAction(operationId); return;
    }
    if (this.busy) { return; }
    const version = this.version; const company = this.companyId;
    this.busy = true; this.error = '';
    let committedNotified = false;
    const publish = async (progress: TeamCreationProgress) => {
      this.progress = progress;
      if (progress.allowanceFunding) { this.remember(progress); this.schedulePoll(); }
      // Refresh the saved local result without ending post-commit recovery.
      // A closed receipt exposes teamId only after a durable local commit, too.
      // Recovering a lost response must publish that commit without resuming work.
      if (['team_committed', 'closed'].includes(progress.phase) && progress.teamId && !committedNotified) {
        committedNotified = true;
        await this.onCommitted(progress.teamId);
      }
    };
    try {
      let progress = await this.api.getWorkforceTeamCreation(company, operationId);
      if (version !== this.version) { return; }
      if (progress.execution === 'background') { this.remember(progress); this.schedulePoll(true); return; }
      await publish(progress);
      if (version !== this.version) { return; }
      // One loop, one POST at a time; every response is fenced to this company/view lifetime.
      for (; !['complete', 'closed'].includes(progress.phase);) {
        if (version !== this.version) { return; }
        let advanced = false;
        try {
          const next = await this.api.continueWorkforceTeamCreation(company, operationId);
          advanced = this.hasAdvanced(progress, next); progress = next;
        }
        catch (error: any) {
          if (error?.status === 403 || error?.status === 401) { throw error; }
          progress = await this.api.getWorkforceTeamCreation(company, operationId);
          // Recover a lost POST response without starting a second team or an overlapping loop.
        }
        if (version !== this.version) { return; }
        await publish(progress);
        if (version !== this.version) { return; }
        if (progress.issues.length || progress.phase === 'needs_review') { break; }
        // Even forward progress yields to input/cancellation. Unchanged or recovered responses retain the existing interval.
        if (!['complete', 'closed'].includes(progress.phase)) { await this.delay(advanced ? 0 : 750); }
      }
      if (progress.phase === 'complete' && progress.teamId) {
        this.recoverable = this.recoverable.filter(item => item.operationId !== operationId);
        this.onComplete(progress.teamId);
      }
    } catch {
      if (version === this.version) { this.error = 'Account setup is paused. Resolve access or connection changes, then Resume. Completed preparation is retained.'; }
    } finally { if (version === this.version) { this.busy = false; } }
  }
  async close(operationId?: string): Promise<void> {
    if (operationId) { await this.backgroundAction(operationId, true); return; }
    if (this.busy || !this.progress) { return; }
    const version = this.version;
    this.busy = true;
    try {
      const value = await this.api.closeWorkforceTeamCreation(this.companyId, this.progress.operationId);
      if (version === this.version) {
        this.progress = value; this.recoverable = this.recoverable.filter(item => item.operationId !== value.operationId);
        if (value.phase === 'closed' && value.teamId) await this.onCommitted(value.teamId);
      }
    } catch { if (version === this.version) { this.error = 'The operation is still active. Wait for its current work to finish before closing recovery.'; } }
    finally { if (version === this.version) { this.busy = false; } }
  }
}
